import React, { useEffect, useId, useRef, useState } from "react";
import FieldError from "../FieldError";
import FieldLabel from "../FieldLabel";
import { useLanguage } from "../../../context/LanguageContext";
import chevronDark from "../../../assets/images/chevron-down-dark.svg";

const getSearchText = (option) =>
  String(option.searchLabel || option.label || option.value || "").toLowerCase();

const SelectField = ({
  id,
  label,
  required = true,
  value,
  onChange,
  onBlur,
  options,
  infoContent,
  infoLabel,
  otherValue = "",
  onOtherChange,
  onOtherBlur,
  error = "",
}) => {
  const { t } = useLanguage();
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const otherInputRef = useRef(null);
  const showOther = value === "other" && typeof onOtherChange === "function";

  const selectableOptions = options.filter((option) => option.value !== "");
  const placeholderOption = options.find((option) => option.value === "");
  const selected = options.find((option) => option.value === value);
  const placeholder = placeholderOption?.label || t("signup.selectShort");
  const displayLabel = selected?.label || placeholder;

  const filteredOptions = selectableOptions.filter((option) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return getSearchText(option).includes(term);
  });

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    searchRef.current?.focus();

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (showOther) otherInputRef.current?.focus();
  }, [showOther]);

  const handleSelect = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setOpen(false);
    setSearch("");
    if (typeof onBlur === "function") {
      onBlur({ target: { value: optionValue } });
    }
  };

  const toggleOpen = () => {
    setOpen((prev) => {
      if (prev) setSearch("");
      return !prev;
    });
  };

  return (
    <div className="signup-form__field">
      <FieldLabel
        htmlFor={id}
        required={required}
        infoContent={infoContent}
        infoLabel={infoLabel}
      >
        {label}
      </FieldLabel>

      <div
        className={`signup-form__select-wrap${open ? " is-open" : ""}`}
        ref={wrapRef}
      >
        <input
          type="hidden"
          id={id}
          name={id}
          value={value}
          required={required}
          tabIndex={-1}
          aria-hidden="true"
        />

        <div
          className={`signup-form__select-face signup-form__input${
            value ? "" : " is-placeholder"
          }${error ? " is-invalid" : ""}`}
          onClick={(event) => {
            if (event.target.closest(".signup-form__select-other")) return;
            toggleOpen();
          }}
        >
          <button
            type="button"
            className={`signup-form__select-choice${showOther ? " is-fixed" : ""}`}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
          >
            {displayLabel}
          </button>

          {showOther ? (
            <>
              <span className="signup-form__select-split" aria-hidden="true">
                |
              </span>
              <input
                ref={otherInputRef}
                id={`${id}-other`}
                name={`${id}-other`}
                className="signup-form__select-other"
                type="text"
                placeholder={t("signup.typeHere")}
                value={otherValue}
                onChange={onOtherChange}
                onBlur={onOtherBlur}
                required={required}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? `${id}-error` : undefined}
                aria-label={t("signup.specify", { label })}
                onClick={(event) => event.stopPropagation()}
              />
            </>
          ) : null}
        </div>

        <img
          className="signup-form__chevron"
          src={chevronDark}
          alt=""
          width={16}
          height={16}
          aria-hidden="true"
        />

        {open ? (
          <div className="signup-form__select-dropdown">
            <input
              ref={searchRef}
              type="text"
              className="signup-form__select-search"
              placeholder={t("signup.search")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t("signup.searchLabel", { label })}
            />
            <ul
              id={listboxId}
              className="signup-form__select-list"
              role="listbox"
              aria-label={label}
            >
              {filteredOptions.length ? (
                filteredOptions.map((option) => (
                  <li key={`${id}-${option.value}`} role="presentation">
                    <button
                      type="button"
                      className={`signup-form__select-option${
                        option.value === value ? " is-selected" : ""
                      }`}
                      role="option"
                      aria-selected={option.value === value}
                      onClick={() => handleSelect(option.value)}
                    >
                      {option.label}
                    </button>
                  </li>
                ))
              ) : (
                <li className="signup-form__select-empty" role="presentation">
                  {t("signup.noResults")}
                </li>
              )}
            </ul>
          </div>
        ) : null}
      </div>
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
};

export default SelectField;
